import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { appointmentTypeLabel } from '../../utils/appointmentTypes';

const API_BASE = '/admin/payments';

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('es-CO', {
        timeZone: 'America/Bogota',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

const fmtCOP = (amount) =>
  typeof amount === 'number'
    ? new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(amount)
    : '—';

const nameOf = (entity) => {
  const p = entity?.user?.profile || entity?.profile || {};
  return [p.firstName, p.lastName].filter(Boolean).join(' ') || '—';
};

const STATUS_CONFIG = {
  pending:   { label: 'Pendiente',  color: 'bg-yellow-100 text-yellow-800' },
  completed: { label: 'Completado', color: 'bg-green-100  text-green-800'  },
  failed:    { label: 'Fallido',    color: 'bg-red-100    text-red-800'    },
  cancelled: { label: 'Cancelado',  color: 'bg-gray-100   text-gray-600'   },
  // La cita se eliminó desde la agenda; el reembolso real se hace por fuera.
  refunded:  { label: 'Reembolsado', color: 'bg-purple-100 text-purple-800' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status || '—', color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// ── FilterBar ─────────────────────────────────────────────────────────────────
const EMPTY_FILTERS = { patient: '', doctor: '', status: '', dateFrom: '', dateTo: '' };

function FilterBar({ filters, onChange, onSearch, loading }) {
  return (
    <form
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
      onSubmit={(e) => { e.preventDefault(); onSearch(); }}
    >
      <input
        className="input-field"
        placeholder="Nombre paciente"
        value={filters.patient}
        onChange={(e) => onChange('patient', e.target.value)}
      />
      <input
        className="input-field"
        placeholder="Nombre doctor"
        value={filters.doctor}
        onChange={(e) => onChange('doctor', e.target.value)}
      />
      <select
        className="input-field"
        value={filters.status}
        onChange={(e) => onChange('status', e.target.value)}
      >
        <option value="">Todos los estados</option>
        {Object.entries(STATUS_CONFIG).map(([val, { label }]) => (
          <option key={val} value={val}>{label}</option>
        ))}
      </select>
      <div className="flex gap-2">
        <input
          type="date"
          className="input-field flex-1"
          value={filters.dateFrom}
          onChange={(e) => onChange('dateFrom', e.target.value)}
          title="Desde"
        />
        <input
          type="date"
          className="input-field flex-1"
          value={filters.dateTo}
          onChange={(e) => onChange('dateTo', e.target.value)}
          title="Hasta"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-50"
        >
          {loading ? 'Buscando…' : 'Buscar'}
        </button>
        <button
          type="button"
          onClick={() => { onChange('__reset'); onSearch(true); }}
          className="px-3 py-2 text-gray-500 hover:text-gray-800 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 transition"
          title="Limpiar filtros"
        >
          ✕
        </button>
      </div>
    </form>
  );
}

// ── PaymentRow ─────────────────────────────────────────────────────────────────
function PaymentRow({ payment, onView, onReceipt }) {
  const appt = payment.appointmentId;
  const patient = appt?.patient;
  const doctor  = appt?.doctor;
  return (
    <tr className="hover:bg-gray-50 transition">
      <td className="px-4 py-3 text-sm text-gray-500">{fmt(payment.createdAt)}</td>
      <td className="px-4 py-3 text-sm font-medium">{nameOf(patient)}</td>
      <td className="px-4 py-3 text-sm">{nameOf(doctor)}</td>
      <td className="px-4 py-3 text-sm font-semibold text-gray-800">{fmtCOP(payment.amount)}</td>
      <td className="px-4 py-3"><StatusBadge status={payment.status} /></td>
      <td className="px-4 py-3 text-sm text-gray-400 font-mono truncate max-w-[140px]">
        {payment.wompiReferenceId || '—'}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-2">
          <button
            onClick={() => onView(payment)}
            className="text-teal-600 hover:text-teal-800 text-sm font-medium transition"
          >
            Ver
          </button>
          {payment.status === 'completed' && (
            <button
              onClick={() => onReceipt(payment._id)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium transition"
            >
              Recibo
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── KV / InfoBox helpers ──────────────────────────────────────────────────────
function InfoBox({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-100 overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
      </div>
      <div className="px-4 py-3 space-y-1">{children}</div>
    </div>
  );
}

function KV({ label, v }) {
  if (!v) return null;
  return (
    <p className="text-sm">
      <span className="text-gray-400">{label}: </span>
      <span className="text-gray-800 font-medium">{v}</span>
    </p>
  );
}

// ── DetailModal ───────────────────────────────────────────────────────────────
function DetailModal({ payment, onClose, onReceipt }) {
  if (!payment) return null;

  const appt          = payment.appointmentId;
  const patient       = appt?.patient;
  const doctor        = appt?.doctor;
  const patientName   = nameOf(patient);
  const doctorName    = nameOf(doctor);
  const patientEmail  = patient?.user?.email || payment.userId?.email || '—';
  const doctorEmail   = doctor?.user?.email  || '—';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="bg-teal-700 text-white rounded-t-2xl px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold">Detalle de Pago</h2>
            <p className="text-teal-200 text-xs mt-0.5 font-mono">{payment._id}</p>
          </div>
          <div className="flex gap-3 items-center">
            <StatusBadge status={payment.status} />
            {payment.status === 'completed' && (
              <button
                onClick={() => onReceipt(payment._id)}
                className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1.5 rounded-lg transition"
              >
                🖨️ Recibo PDF
              </button>
            )}
            <button onClick={onClose} className="text-white/70 hover:text-white text-xl leading-none ml-2">
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5 text-sm">
          {/* Pago */}
          <InfoBox title="Transacción">
            <KV label="Monto"            v={fmtCOP(payment.amount)} />
            <KV label="Estado"           v={STATUS_CONFIG[payment.status]?.label || payment.status} />
            <KV label="Ref. Wompi"       v={payment.wompiReferenceId} />
            <KV label="ID Transacción"   v={payment.transactionId} />
            <KV label="Método de pago"   v={payment.paymentMethod} />
            <KV label="Fecha"            v={fmtDateTime(payment.createdAt)} />
            {payment.status === 'failed' && payment.errorMessage && (
              <KV label="Error" v={payment.errorMessage} />
            )}
            {payment.status === 'refunded' && (
              <>
                <KV label="Reembolsado"          v={fmtDateTime(payment.refundedAt)} />
                <KV label="Motivo del reembolso" v={payment.refundReason} />
              </>
            )}
          </InfoBox>

          {/* Partes */}
          <div className="grid grid-cols-2 gap-4">
            <InfoBox title="Paciente">
              <KV label="Nombre" v={patientName} />
              <KV label="Email"  v={patientEmail} />
            </InfoBox>
            <InfoBox title="Doctor">
              <KV label="Nombre"       v={doctorName} />
              <KV label="Email"        v={doctorEmail} />
              <KV label="Especialidad" v={doctor?.specialization} />
            </InfoBox>
          </div>

          {/* Cita */}
          {appt && (
            <InfoBox title="Cita relacionada">
              <KV label="Fecha"   v={fmtDateTime(appt.scheduledDate)} />
              <KV label="Tipo"    v={appointmentTypeLabel(appt.type)} />
              <KV label="Estado"  v={appt.status} />
              <KV label="Motivo"  v={appt.reason} />
            </InfoBox>
          )}

          {/* Distribución */}
          {appt?.payment?.splitPaid?.doctorAmount != null && (
            <InfoBox title="Distribución">
              <KV label="Al doctor"     v={fmtCOP(appt.payment.splitPaid.doctorAmount)} />
              <KV label="Plataforma"    v={fmtCOP(appt.payment.splitPaid.platformAmount)} />
            </InfoBox>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PaymentsPage ──────────────────────────────────────────────────────────────
export default function PaymentsPage() {
  const [payments,  setPayments]  = useState([]);
  const [total,     setTotal]     = useState(0);
  const [pages,     setPages]     = useState(1);
  const [page,      setPage]      = useState(1);
  const [filters,   setFilters]   = useState(EMPTY_FILTERS);
  const [loading,   setLoading]   = useState(false);
  const [selected,  setSelected]  = useState(null);
  const [error,     setError]     = useState('');

  const debounceRef = useRef(null);

  const fetchPayments = useCallback(async (overrideFilters, overridePage = 1) => {
    setLoading(true);
    setError('');
    const f = overrideFilters ?? filters;
    const p = overridePage;
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (f.status)   params.set('status',   f.status);
      if (f.dateFrom) params.set('dateFrom', f.dateFrom);
      if (f.dateTo)   params.set('dateTo',   f.dateTo);
      if (f.patient.trim()) params.set('patient', f.patient.trim());
      if (f.doctor.trim())  params.set('doctor',  f.doctor.trim());

      const { data } = await api.get(`${API_BASE}?${params}`);
      if (data.success) {
        setPayments(data.data);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPayments(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = (reset = false) => {
    const newFilters = reset ? EMPTY_FILTERS : filters;
    if (reset) setFilters(EMPTY_FILTERS);
    setPage(1);
    fetchPayments(newFilters, 1);
  };

  const handleFilterChange = (key, value) => {
    if (key === '__reset') {
      setFilters(EMPTY_FILTERS);
      return;
    }
    const updated = { ...filters, [key]: value };
    setFilters(updated);

    if (key === 'patient' || key === 'doctor') {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setPage(1);
        fetchPayments(updated, 1);
      }, 400);
    }
  };

  const handleReceipt = async (id) => {
    try {
      const { data } = await api.post(`/admin/payments/${id}/generate-token`);
      if (!data.success) { alert('No se pudo generar el enlace de descarga'); return; }
      const base = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
      window.open(`${base}/api/admin/payments/${id}/receipt?dl_token=${data.token}`, '_blank');
    } catch (err) {
      console.error('handleReceipt:', err);
      alert('Error al generar el enlace de descarga');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Título */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Pagos</h1>
          <p className="text-gray-500 text-sm mt-1">
            {total} registro{total !== 1 ? 's' : ''} en total
          </p>
        </div>

        {/* Filtros */}
        <FilterBar
          filters={filters}
          onChange={handleFilterChange}
          onSearch={handleSearch}
          loading={loading}
        />

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Tabla */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading && payments.length === 0 ? (
            <div className="text-center py-16 text-gray-400">Cargando…</div>
          ) : payments.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-4xl mb-3">💳</p>
              <p className="text-gray-500">No se encontraron pagos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Fecha', 'Paciente', 'Doctor', 'Monto', 'Estado', 'Ref. Wompi', 'Acciones'].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {payments.map((pay) => (
                    <PaymentRow
                      key={pay._id}
                      payment={pay}
                      onView={setSelected}
                      onReceipt={handleReceipt}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginación */}
        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <button
              disabled={page === 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
            >
              ← Anterior
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-600">
              Página {page} de {pages}
            </span>
            <button
              disabled={page === pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {/* Modal detalle */}
      <DetailModal
        payment={selected}
        onClose={() => setSelected(null)}
        onReceipt={handleReceipt}
      />

      <style>{`
        .input-field {
          width: 100%;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 0.45rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s;
          background: white;
        }
        .input-field:focus { border-color: #0d9488; box-shadow: 0 0 0 2px #ccfbf1; }
        select.input-field { cursor: pointer; }
      `}</style>
    </div>
  );
}